/**
 * NightVigil — the run clock for The Small Hours.
 *
 * Every other clock in this codebase is a *world* clock: a rolling 20-minute
 * day seeded from wall time, so whoever loads the game arrives at whatever hour
 * it happens to be. That is right for a world you travel through and wrong for
 * a game whose entire premise is one night. The vigil is a run with a shape —
 * dusk, deep night, dawn, over — and it starts when the player does.
 *
 * It is deliberately *not* a second time system. `PaintedScene` seeds its
 * TimeSystem at dusk (see `dayNightSeedFraction`) and both advance by the same
 * frame delta, so the colour grade, the ambient tint and the fireflies all
 * follow this arc for free and cannot drift from it.
 *
 * The arc, in world-day fractions:
 *
 *     0.65  dusk begins        vigil t=0        the player arrives
 *     0.80  night begins       t=3min           the dark closes in
 *     1.00  dawn begins        t=7min           the sky starts to lift
 *     1.15  day begins         t=10min          the vigil is over
 *
 * Nothing here can fail. Reaching dawn is not conditional — what varies is what
 * the light reached while you kept it, which is the whole design (see
 * docs/design/single-scene-game-spec.md → "The economy").
 */
import { DAY_DURATION_MS, TOD_BOUNDARIES } from '@game/shared';

/** Where the vigil opens: the first minute of dusk. */
export const VIGIL_SEED_FRACTION = TOD_BOUNDARIES.DUSK_START;

/**
 * Length of one vigil. This is the span from dusk to the end of dawn — half a
 * world day — so at real time it runs ten minutes: three of failing light, four
 * of full dark, three of morning. One sitting, with a beginning and an end.
 */
export const VIGIL_DURATION_MS = (1 + TOD_BOUNDARIES.DAY_START - VIGIL_SEED_FRACTION) * DAY_DURATION_MS;

/**
 * Elapsed ms at which the sky begins to lift — and so the length of the dark
 * window the fire actually has to burn against. Exported because that window is
 * the denominator of the whole economy, and the economy tests should read it
 * rather than restate it.
 */
export const DAWN_AT_MS = (1 - VIGIL_SEED_FRACTION) * DAY_DURATION_MS;

/** Elapsed ms at which full dark arrives. */
const NIGHT_AT_MS = (TOD_BOUNDARIES.NIGHT_START - VIGIL_SEED_FRACTION) * DAY_DURATION_MS;

export type VigilStage = 'dusk' | 'night' | 'dawn' | 'over';

export class NightVigil {
  private elapsed = 0;
  private stage: VigilStage = 'dusk';

  /** Fired once, the moment the sky begins to lift. */
  onDawn: (() => void) | null = null;
  /** Fired once, when the vigil is fully over. */
  onComplete: (() => void) | null = null;

  /** Advance by a frame delta in ms. */
  update(delta: number): void {
    if (this.stage === 'over') return;
    this.elapsed += delta;

    const next = this.stageFor(this.elapsed);
    if (next === this.stage) return;
    this.stage = next;
    if (next === 'dawn') this.onDawn?.();
    if (next === 'over') this.onComplete?.();
  }

  private stageFor(elapsed: number): VigilStage {
    if (elapsed >= VIGIL_DURATION_MS) return 'over';
    if (elapsed >= DAWN_AT_MS) return 'dawn';
    if (elapsed >= NIGHT_AT_MS) return 'night';
    return 'dusk';
  }

  getStage(): VigilStage {
    return this.stage;
  }

  /** 0..1 across the whole vigil. */
  getProgress(): number {
    return Math.min(1, this.elapsed / VIGIL_DURATION_MS);
  }

  getElapsedMs(): number {
    return this.elapsed;
  }

  /**
   * How much of the night is left, 0..1, counting only the dark part — dusk and
   * night. Reaches 0 at first light and stays there.
   *
   * This is what the fire is actually *for*: once the sky is doing the work,
   * the fire has nothing left to hold back, and anything driving off "how badly
   * do we need light" should read this rather than raw progress.
   */
  getDarkRemaining(): number {
    if (this.elapsed >= DAWN_AT_MS) return 0;
    return 1 - this.elapsed / DAWN_AT_MS;
  }
}
